////////////////////////////////////////
// # GOLDEN RULE!!!! HONOR ABOVE ALL!!!!
////////////////////////////////////////
// NO NEW FILES!!!!!!!!!
////////////////////////////////////////

/// <reference types="vite/client" />

// I. FILE HEADER [WHITE #FFFFFF]
// ⚪ Main entry point for Taytrom Publishing Co HOME King - Marketing site frontend

// II. GLOBAL ERROR HANDLERS [RED #FF0000]
// 🔴 Catch all errors before React even loads
window.onerror = function(message, source, lineno, colno, error) {
  console.error('🔴 GLOBAL ERROR:', { message, source, lineno, colno, error });
  const errorDiv = document.createElement('div');
  errorDiv.style.cssText = 'position: fixed; top: 0; left: 0; right: 0; background: #f44336; color: white; padding: 20px; z-index: 99999; font-family: monospace; font-size: 14px;';
  errorDiv.innerHTML = `
    <strong>GLOBAL ERROR:</strong><br>
    Message: ${message}<br>
    Source: ${source}:${lineno}:${colno}<br>
    ${error ? `Stack: ${error.stack}` : ''}
  `;
  document.body.insertBefore(errorDiv, document.body.firstChild);
  return false;
};

window.onunhandledrejection = function(event) {
  console.error('🔴 UNHANDLED PROMISE REJECTION:', event.reason);
  const errorDiv = document.createElement('div');
  errorDiv.style.cssText = 'position: fixed; top: 0; left: 0; right: 0; background: #ff9800; color: white; padding: 20px; z-index: 99999; font-family: monospace; font-size: 14px;';
  errorDiv.innerHTML = `
    <strong>UNHANDLED PROMISE REJECTION:</strong><br>
    ${event.reason instanceof Error ? event.reason.message + '<br>' + event.reason.stack : String(event.reason)}
  `;
  document.body.insertBefore(errorDiv, document.body.firstChild);
};

// III. IMPORTS [GREEN #00FF00]
// 🟢 Third-party dependencies
import React from 'react';
import ReactDOM from 'react-dom/client';
import { StrictMode, Component, ErrorInfo, ReactNode } from 'react';

// 🟢 First-party components
import HomepagePod from '../homepage/lobby/pod/TaytromPublishingCo_Kings_Home_SRC_Homepage_Lobby_Pod.js';
import SocraticAiInterviewerPod from '../socratic-ai-interviewer/lobby/pod/TaytromPublishingCo_Kings_Home_SRC_SocraticAIInterviewer_Lobby_Pod.js';
import LibraryLobbyPod from '../library/lobby/pod/TaytromPublishingCo_Kings_Home_SRC_Library_Lobby_Pod.js';
import AboutPod from '../about/lobby/pod/TaytromPublishingCo_Kings_Home_SRC_About_Lobby_Pod.js';
import ContactPod from '../contact/lobby/pod/TaytromPublishingCo_Kings_Home_SRC_Contact_Lobby_Pod.js';
import { taytromPubHomeFrontendLogger } from '../logger/TaytromPublishingCo_Kings_Home_SRC_Logger.js';

// IV. INDEPENDENT CONSTANTS [TEAL #00FFFF]
// 🔵 Configuration constants
const ROOT_ELEMENT_ID: string = 'root';
const ENV_MODE: string = import.meta.env.MODE || 'development';

const PERFORMANCE_TARGETS: Record<string, number> = {
  FIRST_PAINT_MS: 300,
  INTERACTIVE_MS: 1000,
  BOOK_LOAD_MS: 200
};

// Route configuration for maintainability
const ROUTES: Record<string, React.ComponentType> = {
  '/': HomepagePod,
  '/ai-interviewer': SocraticAiInterviewerPod,
  '/library': LibraryLobbyPod,
  '/books': LibraryLobbyPod,
  '/about': AboutPod,
  '/contact': ContactPod,
};

// V. FUNCTION/CLASS NAME [BLUE #0000FF] - OUTPUT DECLARATION
// 🔵 Error Boundary Component
interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    // ALWAYS log to console first (even if logger fails)
    console.error('🔴 ERROR BOUNDARY - getDerivedStateFromError:', error);
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // ALWAYS log to console first (even if logger fails)
    console.error('🔴 ERROR BOUNDARY - componentDidCatch:', {
      error,
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack
    });

    // Try to use logger (may fail if logger is broken)
    try {
      taytromPubHomeFrontendLogger.error('React Error Boundary caught error', {
        error: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack
      });
    } catch (loggerError) {
      console.error('🔴 Logger itself failed:', loggerError);
    }

    this.setState({ error, errorInfo });
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #0a0a0a 0%, #121212 50%, #1a1a1a 100%)',
          color: '#ffffff',
          padding: '40px',
          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
        }}>
          <div style={{
            maxWidth: '600px',
            textAlign: 'center',
            background: 'linear-gradient(135deg, rgba(30,30,30,0.95) 0%, rgba(20,20,20,0.85) 100%)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '16px',
            padding: '48px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)'
          }}>
            <h1 style={{ fontSize: '32px', marginBottom: '16px', color: '#f44336' }}>
              ⚠️ Something went wrong
            </h1>
            <p style={{ fontSize: '16px', marginBottom: '24px', color: 'rgba(255,255,255,0.8)' }}>
              We encountered an unexpected error. Please try refreshing the page.
            </p>
            {this.state.error && (
              <details open style={{
                marginTop: '24px',
                textAlign: 'left',
                background: 'rgba(0,0,0,0.3)',
                padding: '16px',
                borderRadius: '8px',
                fontSize: '14px',
                fontFamily: 'monospace'
              }}>
                <summary style={{ cursor: 'pointer', marginBottom: '12px', color: '#ff9800' }}>
                  Error Details (Click to collapse)
                </summary>
                <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: '#f44336' }}>
                  {this.state.error.toString()}
                  {this.state.error.stack}
                  {this.state.errorInfo && '\n\nComponent Stack:\n' + this.state.errorInfo.componentStack}
                </pre>
              </details>
            )}
            <button
              onClick={() => window.location.reload()}
              style={{
                marginTop: '24px',
                padding: '14px 32px',
                fontSize: '16px',
                fontWeight: 600,
                borderRadius: '10px',
                border: 'none',
                background: 'linear-gradient(135deg, #20b2aa 0%, #1c9d96 100%)',
                color: '#ffffff',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(32,178,170,0.3)'
              }}
            >
              🔄 Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// 🔵 Loading Component (exported for future use in loading states)
export const LoadingScreen: React.FC = () => (
  <div style={{
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #0a0a0a 0%, #121212 50%, #1a1a1a 100%)',
    color: '#ffffff'
  }}>
    <div style={{ textAlign: 'center' }}>
      <div style={{
        width: '60px',
        height: '60px',
        border: '4px solid rgba(32,178,170,0.2)',
        borderTop: '4px solid #20b2aa',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
        margin: '0 auto 24px'
      }} />
      <p style={{ fontSize: '18px', color: 'rgba(255,255,255,0.8)' }}>
        Loading Taytrom Publishing Co...
      </p>
    </div>
    <style>{`
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);

// 🔵 Not Found Component
const NotFoundPage: React.FC = () => (
  <div style={{
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #0a0a0a 0%, #121212 50%, #1a1a1a 100%)',
    color: '#ffffff',
    padding: '40px',
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
  }}>
    <div style={{
      maxWidth: '600px',
      textAlign: 'center',
      background: 'linear-gradient(135deg, rgba(30,30,30,0.95) 0%, rgba(20,20,20,0.85) 100%)',
      backdropFilter: 'blur(20px)',
      border: '1px solid rgba(255,255,255,0.1)',
      borderRadius: '16px',
      padding: '48px',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)'
    }}>
      <h1 style={{ fontSize: '72px', marginBottom: '16px' }}>404</h1>
      <h2 style={{ fontSize: '32px', marginBottom: '16px', color: '#20b2aa' }}>
        Page Not Found
      </h2>
      <p style={{ fontSize: '16px', marginBottom: '32px', color: 'rgba(255,255,255,0.8)' }}>
        The page you're looking for doesn't exist or has been moved.
      </p>
      <a
        href="/"
        style={{
          display: 'inline-block',
          padding: '14px 32px',
          fontSize: '16px',
          fontWeight: 600,
          borderRadius: '10px',
          border: 'none',
          background: 'linear-gradient(135deg, #20b2aa 0%, #1c9d96 100%)',
          color: '#ffffff',
          textDecoration: 'none',
          boxShadow: '0 4px 12px rgba(32,178,170,0.3)'
        }}
      >
        🏠 Back to Homepage
      </a>
    </div>
  </div>
);

// 🔵 Main App Component for routing
const AppComponent: React.FC = () => {

  // VI. DECLARATIONS [PURPLE #FF00FF] - REQUIREMENTS SETUP
  const pathname = window.location.pathname;

  // VIII. OPERATIONS [ORANGE #FFA500] - REQUIREMENTS PROCESSING
  // Enhanced routing with ROUTES configuration and 404 handling
  let ComponentToRender: React.ComponentType;

  // Check exact match first
  if (ROUTES[pathname]) {
    ComponentToRender = ROUTES[pathname];
  }
  // Check prefix matches for nested routes
  else if (pathname.startsWith('/library')) {
    ComponentToRender = ROUTES['/library'];
  }
  else if (pathname.startsWith('/books')) {
    ComponentToRender = ROUTES['/books'];
  }
  // 404 fallback
  else {
    ComponentToRender = NotFoundPage;
    taytromPubHomeFrontendLogger.warn('Route not found, rendering 404', { pathname });
  }

  // IX. PRE-RENDER [RED #FF0000] - REQUIREMENTS ASSEMBLY
  taytromPubHomeFrontendLogger.info('Rendering route', {
    pathname,
    matched: ComponentToRender !== NotFoundPage
  });

  // X. RENDER/EXPORTS [BLACK #000000] - OUTPUT DELIVERY
  return (
    <StrictMode>
      <ErrorBoundary>
        <ComponentToRender />
      </ErrorBoundary>
    </StrictMode>
  );
};

// 🔵 Global Error Handlers
const setupGlobalErrorHandlers = (): void => {
  // Handle uncaught errors
  window.addEventListener('error', (event) => {
    taytromPubHomeFrontendLogger.error('Uncaught error', {
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      stack: event.error?.stack
    });
  });

  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    taytromPubHomeFrontendLogger.error('Unhandled promise rejection', {
      reason: event.reason,
      promise: event.promise
    });
  });

  taytromPubHomeFrontendLogger.debug('Global error handlers registered');
};

// 🔵 Performance Monitoring
const monitorPerformance = (): void => {
  if ('performance' in window && 'getEntriesByType' in performance) {
    window.addEventListener('load', () => {
      setTimeout(() => {
        const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
        if (navigation) {
          const metrics = {
            dns: Math.round(navigation.domainLookupEnd - navigation.domainLookupStart),
            tcp: Math.round(navigation.connectEnd - navigation.connectStart),
            request: Math.round(navigation.responseStart - navigation.requestStart),
            response: Math.round(navigation.responseEnd - navigation.responseStart),
            domParse: Math.round(navigation.domInteractive - navigation.responseEnd),
            domContentLoaded: Math.round(navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart),
            domComplete: Math.round(navigation.domComplete - navigation.domContentLoadedEventEnd),
            loadEvent: Math.round(navigation.loadEventEnd - navigation.loadEventStart),
            totalLoad: Math.round(navigation.loadEventEnd - navigation.fetchStart)
          };

          taytromPubHomeFrontendLogger.info('Performance metrics', metrics);

          if (metrics.totalLoad > PERFORMANCE_TARGETS.INTERACTIVE_MS) {
            taytromPubHomeFrontendLogger.warn('Total load time exceeded target', {
              target: PERFORMANCE_TARGETS.INTERACTIVE_MS,
              actual: metrics.totalLoad,
              breakdown: metrics
            });
          }
        }
      }, 0);
    });
  }
};

// 🔵 Main initialization with enhanced error handling
const initializeApp = (): void => {
  const startTime = performance.now();

  try {
    // Setup global error handlers first
    setupGlobalErrorHandlers();

    // Setup performance monitoring
    monitorPerformance();

    // Find root element
    const rootElement = document.getElementById(ROOT_ELEMENT_ID);

    if (!rootElement) {
      const errorMessage = `❌ Root element with id "${ROOT_ELEMENT_ID}" not found`;
      taytromPubHomeFrontendLogger.error(errorMessage);

      // Render user-friendly error UI instead of throwing
      document.body.innerHTML = `
        <div style="
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #0a0a0a 0%, #121212 50%, #1a1a1a 100%);
          color: #ffffff;
          padding: 40px;
          font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        ">
          <div style="
            max-width: 600px;
            text-align: center;
            background: linear-gradient(135deg, rgba(30,30,30,0.95) 0%, rgba(20,20,20,0.85) 100%);
            backdrop-filter: blur(20px);
            border: 1px solid rgba(255,255,255,0.1);
            border-radius: 16px;
            padding: 48px;
            box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
          ">
            <h1 style="font-size: 32px; margin-bottom: 16px; color: #f44336;">
              ⚠️ Initialization Error
            </h1>
            <p style="font-size: 16px; margin-bottom: 24px; color: rgba(255,255,255,0.8);">
              Failed to initialize the application. Please ensure your HTML has a root element with id="root".
            </p>
            <button
              onclick="window.location.reload()"
              style="
                padding: 14px 32px;
                font-size: 16px;
                font-weight: 600;
                border-radius: 10px;
                border: none;
                background: linear-gradient(135deg, #20b2aa 0%, #1c9d96 100%);
                color: #ffffff;
                cursor: pointer;
                box-shadow: 0 4px 12px rgba(32,178,170,0.3);
              "
            >
              🔄 Reload Page
            </button>
          </div>
        </div>
      `;
      return;
    }

    // Create React root and render app
    const root = ReactDOM.createRoot(rootElement);
    root.render(<AppComponent />);

    const initTime = performance.now() - startTime;

    taytromPubHomeFrontendLogger.info('Taytrom Publishing Co HOME King initialized', {
      environment: ENV_MODE,
      initTime: `${initTime.toFixed(2)}ms`,
      performanceTargets: PERFORMANCE_TARGETS,
      pathname: window.location.pathname,
      userAgent: navigator.userAgent,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      }
    });

    if (initTime > PERFORMANCE_TARGETS.FIRST_PAINT_MS) {
      taytromPubHomeFrontendLogger.warn('Initialization time exceeded first paint target', {
        target: PERFORMANCE_TARGETS.FIRST_PAINT_MS,
        actual: initTime
      });
    }
  } catch (error) {
    taytromPubHomeFrontendLogger.error('Critical initialization error', {
      error: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined
    });

    // Render critical error UI
    document.body.innerHTML = `
      <div style="
        min-height: 100vh;
        display: flex;
        align-items: center;
        justify-content: center;
        background: linear-gradient(135deg, #0a0a0a 0%, #121212 50%, #1a1a1a 100%);
        color: #ffffff;
        padding: 40px;
        font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      ">
        <div style="
          max-width: 600px;
          text-align: center;
          background: linear-gradient(135deg, rgba(30,30,30,0.95) 0%, rgba(20,20,20,0.85) 100%);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 16px;
          padding: 48px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
        ">
          <h1 style="font-size: 32px; margin-bottom: 16px; color: #f44336;">
            ⚠️ Critical Error
          </h1>
          <p style="font-size: 16px; margin-bottom: 24px; color: rgba(255,255,255,0.8);">
            A critical error occurred during initialization. Please try refreshing the page.
          </p>
          ${ENV_MODE === 'development' && error instanceof Error ? `
            <details style="
              margin-top: 24px;
              text-align: left;
              background: rgba(0,0,0,0.3);
              padding: 16px;
              border-radius: 8px;
              font-size: 14px;
              font-family: monospace;
            ">
              <summary style="cursor: pointer; margin-bottom: 12px; color: #ff9800;">
                Error Details (Development Only)
              </summary>
              <pre style="white-space: pre-wrap; word-break: break-word; color: #f44336;">
${error.message}
${error.stack || ''}
              </pre>
            </details>
          ` : ''}
          <button
            onclick="window.location.reload()"
            style="
              margin-top: 24px;
              padding: 14px 32px;
              font-size: 16px;
              font-weight: 600;
              border-radius: 10px;
              border: none;
              background: linear-gradient(135deg, #20b2aa 0%, #1c9d96 100%);
              color: #ffffff;
              cursor: pointer;
              box-shadow: 0 4px 12px rgba(32,178,170,0.3);
            "
          >
            🔄 Reload Page
          </button>
        </div>
      </div>
    `;
  }
};

// 🔵 Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeApp);
} else {
  initializeApp();
}
